import { FilterTextField } from "../../../common/ClearControls";
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import MainCard from "../../components/MainCard";
import Sidebar from "../../menu-items/Sidebar";
import axios from "axios";
import { Autocomplete } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { PieChart, Pie, Cell } from "recharts";
import Cookies from "js-cookie";

const VideoPercentageReport = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);

  const [consentBundles, setConsentBundles] = useState([]);
  const [selectedConsentBundle, setSelectedConsentBundle] = useState("");

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState("");
  const [histogramData, setHistogramData] = useState([]);
  const [groupBy, setGroupBy] = useState("consent_bundle");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedLogs, setSelectedLogs] = useState([]);
  // const [pieData, setPieData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [offset, setOffset] = useState(1); // offset starts from 1

  const pieColors = ["#4caf50", "#f44336"];
  const [pieData, setPieData] = useState([]);

  useEffect(() => {
    const fetchVideoInclusionData = async () => {
      const token = axios.defaults.headers.common.Authorization;
      const url = "/admin/reports/consent_bundle_video_inclusion";

       const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

      try {
        const response = await axios.get(url, config);
        const {
          consent_bundles_with_videos_count: withVideos,
          consent_bundles_without_videos_count: withoutVideos,
        } = response.data;

        const total = withVideos + withoutVideos;
        const pieFormatted = [
          {
            name: "With Videos",
            value: parseFloat(((withVideos / total) * 100).toFixed(2)),
          },
          {
            name: "Without Videos",
            value: parseFloat(((withoutVideos / total) * 100).toFixed(2)),
          },
        ];

        setPieData(pieFormatted);
      } catch (error) {
        console.error("Error fetching video inclusion data:", error);
      }
    };

    fetchVideoInclusionData();
  }, []);

  useEffect(() => {
    const fetchConsentBundles = async () => {
      try {
        const token = axios.defaults.headers.common.Authorization;
        const baseUrl = process.env.REACT_APP_BASE_URL;
        const url = `${baseUrl}admin/dropdown/consent_bundles`;

        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

        const response = await axios.get(url, config);
        setConsentBundles(response.data); // should be an array of { value, label }
      } catch (error) {
        console.error("Error fetching consent bundles:", error);
      }
    };

    fetchConsentBundles();
  }, []);

  // Fetch users list
  useEffect(() => {
    const fetchUsers = async () => {
      try {
       const token = axios.defaults.headers.common.Authorization;
        const baseUrl = process.env.REACT_APP_BASE_URL;
        const url = `${baseUrl}admin/dropdown/users`;

         const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

        const response = await axios.get(url, config);
        setUsers(response.data); // should be an array of { value, label }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = axios.defaults.headers.common.Authorization;
        const baseUrl = process.env.REACT_APP_BASE_URL;
        const url = `${baseUrl}admin/dropdown/patients`;

         const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

        const response = await axios.get(url, config);
        setPatients(response.data); // should be an array of { value, label }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchPatients();
  }, []);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const fetchData = async () => {
     const token = axios.defaults.headers.common.Authorization;
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const params = new URLSearchParams({
      limit: rowsPerPage.toString(),
      offset: offset === 0 ? "1" : offset.toString(),
      groupBy: groupBy,
    });

    if (selectedConsentBundle)
      params.append("consentBundle", selectedConsentBundle);
    if (selectedUser) params.append("user", selectedUser);
    if (selectedPatient) params.append("patient", selectedPatient);
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);

    const url = `${baseUrl}admin/reports/video_watch_percentage_report?${params.toString()}`;

     const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

    try {
      const response = await axios.get(url, config);
      const rawData = response.data.results;
      setTotalRecords(response.data.total);

      const processedData = rawData.map((row) => ({
        ...row,
        watch_percentage: Math.round(row.watch_percentage * 100),
      }));

      setData(processedData);

      const bins = Array.from(
        { length: 10 },
        (_, i) => `${i * 10}-${i * 10 + 10}`
      );

      const histogramData: Record<string, Record<string, number>> = {};

      processedData.forEach((row) => {
        const bundle =
          row.content_bundle ||
          (groupBy === "video" ? "no.of.videos" : "no.of.Consent bundles");
        const bucketIndex = Math.floor(row.watch_percentage / 10);
        const rangeLabel = `${bucketIndex * 10}-${bucketIndex * 10 + 10}`;

        if (!histogramData[bundle]) histogramData[bundle] = {};
        if (!histogramData[bundle][rangeLabel])
          histogramData[bundle][rangeLabel] = 0;

        histogramData[bundle][rangeLabel]++;
      });

      const histogramArray = bins.map((range) => {
        const entry: any = { range };
        for (const bundle in histogramData) {
          entry[bundle] = histogramData[bundle][range] || 0;
        }
        return entry;
      });

      setHistogramData(histogramArray);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleRowClick = (row) => {
    setSelectedLogs(row.logs || []);
    setOpenModal(true);
  };

  useEffect(() => {
    setPage(0);
    fetchData();
  }, [
    selectedConsentBundle,
    selectedUser,
    groupBy,
    selectedPatient,
    dateFrom,
    dateTo,
  ]);

  const downloadAsCSV = () => {
    const csvRows = [];

    const isGroupByVideo = groupBy === "video";
    const headers = isGroupByVideo
      ? [
          "#",
          "Video Title",
          "Consent Bundle Code",
          "Consent Bundle Status",
          "Patient Name",
          "Practice Name",
          "Watch Percentage",
        ]
      : [
          "#",
          "Consent Bundle Code",
          "Consent Bundle Status",
          "Patient Name",
          "Practice Name",
          "Watch Percentage",
        ];

    csvRows.push(headers.join(","));

    data.forEach((row, index) => {
      const rowData = isGroupByVideo
        ? [
            index + 1,
            row.video_title || "",
            row.consent_bundle_code || "",
            row.consent_bundle_status || "",
            row.patient_name || "",
            row.practice_name || "",
            row.watch_percentage != null ? `${row.watch_percentage}%` : "",
          ]
        : [
            index + 1,
            row.consent_bundle_code || "",
            row.consent_bundle_status || "",
            row.patient_name || "",
            row.practice_name || "",
            row.watch_percentage != null ? `${row.watch_percentage}%` : "",
          ];

      csvRows.push(
        rowData.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      );
    });

    const csvData = csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Video_Percentage_Report.csv";
    link.click();
  };

  const handleChangePage = (event, newPage) => {
    setOffset(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setOffset(1);
  };

  const sortedData = [...data].sort((a, b) => {
    if (orderBy) {
      const valA = a[orderBy] ?? "";
      const valB = b[orderBy] ?? "";
      if (typeof valA === "number") {
        return order === "asc" ? valA - valB : valB - valA;
      } else {
        return order === "asc"
          ? valA.toString().localeCompare(valB.toString())
          : valB.toString().localeCompare(valA.toString());
      }
    }
    return 0;
  });

  const COLORS = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7300",
    "#d0ed57",
    "#a4de6c",
    "#8dd1e1",
  ];
  const displayedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ border: "1px solid #ccc", padding: 5, height: "100vh" }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.75}>
        <Grid item xs={12} md={3} lg={2}>
          <Sidebar />
        </Grid>

        <Grid item xs={12} md={9} lg={10}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", fontSize: "2rem" }}
            >
              Video Watch Percentage Report
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginLeft: 2 }}
              onClick={downloadAsCSV}
            >
              Download as CSV
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 5,
              mb: 4,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Autocomplete
              options={consentBundles}
              getOptionLabel={(option) => option.label || ""}
              value={
                consentBundles.find(
                  (bundle) => bundle.value === selectedConsentBundle
                ) || null
              }
              onChange={(event, newValue) =>
                setSelectedConsentBundle(newValue ? newValue.value : "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Consent Bundle"
                  size="small"
                  sx={{ minWidth: 250 }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
            />
            <Autocomplete
              options={[{ label: "All", value: "" }, ...users]}
              getOptionLabel={(option) => option.label || ""}
              value={
                users.find((user) => user.value === selectedUser) || {
                  label: "All",
                  value: "",
                }
              }
              onChange={(event, newValue) =>
                setSelectedUser(newValue ? newValue.value : "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="User"
                  size="small"
                  sx={{ minWidth: 250 }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
            />

            <Autocomplete
              options={[{ label: "All", value: "" }, ...patients]}
              getOptionLabel={(option) => option.label || ""}
              value={
                users.find((user) => user.value === selectedPatient) || {
                  label: "All",
                  value: "",
                }
              }
              onChange={(event, newValue) =>
                setSelectedPatient(newValue ? newValue.value : "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Patient"
                  size="small"
                  sx={{ minWidth: 250 }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
            />

            <FilterTextField active={groupBy !== "consent_bundle"} onClear={() => setGroupBy("consent_bundle")} clearLabel="Clear group by filter"
              select
              label="Group By"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="consent_bundle">Consent Bundle</MenuItem>
              <MenuItem value="video">Video</MenuItem>
            </FilterTextField>

            {/* <TextField
                              label="Date From"
                              type="date"
                              value={dateFrom}
                              onChange={(e) => setDateFrom(e.target.value)}
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
            
                            <TextField
                              label="Date To"
                              type="date"
                              value={dateTo}
                              onChange={(e) => setDateTo(e.target.value)}
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            /> */}

            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                fetchData();
                setSelectedConsentBundle("");
                setSelectedUser("");
                setSelectedPatient("");
                setGroupBy("consent_bundle");
              }}
              size="small"
            >
              Clear Filters
            </Button>
            <Box sx={{ width: "100%", mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Stacked Watch Percentage Distribution
              </Typography>
            </Box>

            {histogramData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={histogramData} mb>
                  <XAxis
                    dataKey="range"
                    label={{
                      value: "Video Duration (%)",
                      position: "insideBottom",
                      offset: -5,
                    }}
                  />
                  <YAxis
                    label={{
                      value: "Number of Consent Bundles",
                      angle: -90,
                      position: "insideLeft",
                      offset: 6,
                      dy: 80,
                    }}
                  />
                  <Tooltip />
                  <Legend verticalAlign="top" align="right" />
                  {Object.keys(histogramData[0])
                    .filter((key) => key !== "range")
                    .map((key, idx) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="a"
                        fill={COLORS[idx % COLORS.length]}
                      />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                No data available for chart.
              </Typography>
            )}
          </Box>

          <MainCard title="">
            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={offset - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "15px",
                boxShadow: 3,
                overflow: "hidden",
                marginTop: 2,
              }}
            >
              <Table sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1976d2", color: "#fff" }}>
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      #
                    </TableCell>
                    {groupBy === "video" && (
                      <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                        Video Title
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      Consent Bundle Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      Consent Bundle Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      Patient Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                      Practice Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={() => handleSort("watch_percentage")}
                    >
                      Watch Percentage
                      {orderBy === "watch_percentage" ? (
                        order === "asc" ? (
                          <ArrowUpwardIcon
                            sx={{ fontSize: 16, ml: 0.5, color: "white" }}
                          />
                        ) : (
                          <ArrowDownwardIcon
                            sx={{ fontSize: 16, ml: 0.5, color: "white" }}
                          />
                        )
                      ) : (
                        // default (not active) icon with lower opacity
                        <ArrowDownwardIcon
                          sx={{
                            fontSize: 16,
                            ml: 0.5,
                            opacity: 0.3,
                            color: "white",
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    
                    displayedData.map((row, index) => (
                      
                      <TableRow
                        key={index}
                        onClick={() => handleRowClick(row)}
                        style={{ cursor: "pointer" }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        {groupBy === "video" && (
                          <TableCell>{row.video_title}</TableCell>
                        )}
                        <TableCell>{row.consent_bundle_code}</TableCell>
                        <TableCell>{row.consent_bundle_status}</TableCell>
                        <TableCell>{row.patient_name}</TableCell>
                        <TableCell>{row.practice_name}</TableCell>
                        <TableCell>{row.watch_percentage}%</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Dialog
              open={openModal}
              onClose={() => setOpenModal(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Log Details</DialogTitle>
              <DialogContent>
                {selectedLogs.length > 0 ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Video</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Video Timestamp</TableCell>
                        <TableCell>Action Timestamp</TableCell>
                        <TableCell>ip address</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedLogs.map((log, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{log.video_title}</TableCell>
                          <TableCell>{log.event}</TableCell>
                          <TableCell>
                            {log.video_timestamp.toFixed(2)}s
                          </TableCell>
                          <TableCell>
                            {new Date(log.log_timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>{log.ip_address}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p>No logs available for this record.</p>
                )}
              </DialogContent>
            </Dialog>

            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={offset - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </MainCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VideoPercentageReport;
